import type { Dayjs } from "dayjs";
import type {
  FlatHierarchyRow,
  HierarchyTablePeriodLabels,
  HierarchyTreeNode,
} from "../../Types/types";

export const formatPercent = (value: number): string => `${value}%`;

export const formatHeaderPeriodRange = (start: Dayjs, end: Dayjs): string => {
  const normalizedStart = start.startOf("month");
  const normalizedEnd = end.startOf("month");

  if (normalizedStart.isSame(normalizedEnd, "month")) {
    return `(${normalizedStart.format("MMM YYYY")})`;
  }

  return `(${normalizedStart.format("MMM YYYY")}-${normalizedEnd.format("MMM YYYY")})`;
};

export const buildPeriodLabelsFromDateRange = (
  dateStart: Dayjs,
  dateEnd: Dayjs,
): HierarchyTablePeriodLabels => {
  const selectedStart = dateStart.startOf("month");
  const selectedEnd = dateEnd.startOf("month");
  const selectedPeriod = formatHeaderPeriodRange(selectedStart, selectedEnd);

  const monthSpan = selectedEnd.diff(selectedStart, "month") + 1;
  const cm1End = selectedStart.subtract(1, "month");
  const cm1Start = cm1End.subtract(monthSpan - 1, "month");
  const cm1Period = formatHeaderPeriodRange(cm1Start, cm1End);

  return {
    revenueCm1Period: cm1Period,
    revenuePeriod: selectedPeriod,
    gmPeriod: selectedPeriod,
    billedHoursCm1Period: cm1Period,
    billedHoursPeriod: selectedPeriod,
  };
};

export const hasChildren = (node: HierarchyTreeNode): boolean =>
  !!node.is_expandable ||
  (Array.isArray(node.children) && node.children.length > 0);

export const collectExpandableIds = (
  nodes: HierarchyTreeNode[],
  maxDepth = 1,
): string[] => {
  const ids: string[] = [];

  const walk = (list: HierarchyTreeNode[], depth: number) => {
    list.forEach((node) => {
      if (hasChildren(node) && depth <= maxDepth) {
        ids.push(node.entity_id);
        walk(node.children ?? [], depth + 1);
      }
    });
  };

  walk(nodes, 0);
  return ids;
};

export const flattenVisibleRows = (
  nodes: HierarchyTreeNode[],
  expandedIds: Set<string>,
  depth = 0,
  parentKey = "",
): FlatHierarchyRow[] => {
  const rows: FlatHierarchyRow[] = [];

  nodes.forEach((node, index) => {
    const currentKey = parentKey
      ? `${parentKey}_${node.entity_id}_${index}`
      : `${node.entity_id}_${index}`;
    rows.push({ node, depth, rowKey: currentKey });
    if (hasChildren(node) && expandedIds.has(node.entity_id)) {
      rows.push(
        ...flattenVisibleRows(
          node.children ?? [],
          expandedIds,
          depth + 1,
          currentKey,
        ),
      );
    }
  });

  return rows;
};
